import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Stack,
  CircularProgress,
} from "@mui/material";
import api from "../api";
import { useToast } from "./ToastProvider";
import { useCreateBankAccount, useUpdateBankAccount } from "../hooks/queries";
import { COLORS } from "../theme/tokens";

// Light-only tokens (same key names UserFormBody uses).
const colors = {
  text: COLORS.ink,
  textMuted: COLORS.muted,
  textFaint: COLORS.faint,
  cardBg: COLORS.surface,
  cardBorder: COLORS.hairline,
  cardBorderHover: COLORS.borderHover,
  hover: COLORS.tile,
  divider: COLORS.hairline,
};

// Country / GST option lists — matching the reference form.
const COUNTRY_OPTIONS = ["India", "United Arab Emirates"];
const GST_OPTIONS = ["Yes", "No"];

const labelSx = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: colors.textMuted,
  mb: 0.75,
};

// A single labelled field (with the red * for required ones). Defined at module
// scope — NOT inside the component — so it keeps a stable identity across
// renders. (A component defined inline would be re-created on every keystroke,
// remounting its input and losing focus after one character.)
function Field({ label, req, children }) {
  return (
    <Box>
      <Typography component="label" sx={labelSx}>
        {label}
        {req && <Box component="span" sx={{ color: COLORS.danger, ml: 0.4 }}>*</Box>}
      </Typography>
      {children}
    </Box>
  );
}

const EMPTY = {
  country: "",
  accountName: "",
  bank: "",
  accountNumber: "",
  ibanNumber: "",
  ifscCode: "",
  branch: "",
  location: "",
  gst: "",
  gpayPaytmPhonepe: "",
};

// Create/Edit Bank Account form body. Rendered inside BankAccountModal (spec §5
// Forms & Modals). Manages its own state + save; calls onSuccess({ isEdit })
// after a successful create/update.
//
// Props:
//   accountId — null/undefined = create (POST); an id = edit (loads + PUT).
//   onSuccess — called after a successful save.
//   onCancel  — called when the user clicks Close.
function BankAccountFormBody({ accountId = null, onSuccess, onCancel }) {
  const toast = useToast();
  const isEdit = accountId != null;

  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const createAccount = useCreateBankAccount();
  const updateAccount = useUpdateBankAccount();

  // Edit mode: load the existing account once.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const acc = await api.get(`/bank-accounts/${accountId}`);
        if (cancelled) return;
        setForm({
          country: acc.country || "",
          accountName: acc.accountName || "",
          bank: acc.bank || "",
          accountNumber: acc.accountNumber || "",
          ibanNumber: acc.ibanNumber || "",
          ifscCode: acc.ifscCode || "",
          branch: acc.branch || "",
          location: acc.location || "",
          gst: acc.gst || "",
          gpayPaytmPhonepe: acc.gpayPaytmPhonepe || "",
        });
      } catch (err) {
        if (!cancelled) toast.error(err.message || "Failed to load bank account");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, isEdit, toast]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    // Required fields (marked * in the reference form).
    const required = [
      ["country", "Country"],
      ["accountName", "Account Name"],
      ["bank", "Bank"],
      ["accountNumber", "Account Number"],
      ["ifscCode", "IFSC Code"],
      ["branch", "Branch"],
      ["location", "Location"],
      ["gst", "GST"],
    ];
    for (const [key, label] of required) {
      if (!String(form[key] || "").trim()) {
        toast.error(`${label} is required`);
        return;
      }
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateAccount.mutateAsync({ id: accountId, data: form });
      } else {
        await createAccount.mutateAsync(form);
      }
      toast.success(isEdit ? "Bank account updated" : "Bank account added");
      onSuccess?.({ isEdit });
    } catch (err) {
      toast.error(err.message || "Failed to save bank account");
      setSaving(false);
    }
  }

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: "10px",
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: COLORS.primary },
    },
    "& input, & .MuiSelect-select": {
      color: colors.text,
      "&::placeholder": { color: colors.textFaint, opacity: 1 },
    },
  };
  const cardSx = {
    background: "#ffffff",
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
    p: 3,
  };
  const gridSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    gap: 2.25,
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: COLORS.primary }} />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Box sx={cardSx}>
        <Box sx={gridSx}>
          <Field label="Country" req>
            <TextField
              select
              fullWidth
              value={form.country}
              onChange={(e) => setField("country", e.target.value)}
              sx={fieldSx}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="" disabled>
                Select Country
              </MenuItem>
              {COUNTRY_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Field>

          <Field label="Account Name" req>
            <TextField
              fullWidth
              placeholder="Enter Account Name"
              value={form.accountName}
              onChange={(e) => setField("accountName", e.target.value)}
              sx={fieldSx}
            />
          </Field>

          <Field label="Bank" req>
            <TextField
              fullWidth
              placeholder="Enter Bank Name"
              value={form.bank}
              onChange={(e) => setField("bank", e.target.value)}
              sx={fieldSx}
            />
          </Field>

          <Field label="Account Number" req>
            <TextField
              fullWidth
              placeholder="Enter Account Number"
              value={form.accountNumber}
              onChange={(e) => setField("accountNumber", e.target.value)}
              sx={fieldSx}
            />
          </Field>

          <Field label="IFSC Code" req>
            <TextField
              fullWidth
              placeholder="Enter IFSC Code"
              value={form.ifscCode}
              onChange={(e) => setField("ifscCode", e.target.value)}
              sx={fieldSx}
            />
          </Field>

          <Field label="IBAN Number">
            <TextField
              fullWidth
              placeholder="Enter IBAN Number"
              value={form.ibanNumber}
              onChange={(e) => setField("ibanNumber", e.target.value)}
              sx={fieldSx}
            />
          </Field>

          <Field label="Branch" req>
            <TextField
              fullWidth
              placeholder="Enter Branch"
              value={form.branch}
              onChange={(e) => setField("branch", e.target.value)}
              sx={fieldSx}
            />
          </Field>

          <Field label="Location" req>
            <TextField
              fullWidth
              placeholder="Enter Location"
              value={form.location}
              onChange={(e) => setField("location", e.target.value)}
              sx={fieldSx}
            />
          </Field>

          <Field label="GST" req>
            <TextField
              select
              fullWidth
              value={form.gst}
              onChange={(e) => setField("gst", e.target.value)}
              sx={fieldSx}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              {GST_OPTIONS.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </TextField>
          </Field>

          <Field label="UPI (UPI ID / Phone No)">
            <TextField
              fullWidth
              placeholder="Enter UPI ID / Phone No"
              value={form.gpayPaytmPhonepe}
              onChange={(e) => setField("gpayPaytmPhonepe", e.target.value)}
              sx={fieldSx}
            />
          </Field>
        </Box>
      </Box>

      {/* Actions */}
      <Stack
        direction="row"
        spacing={1.5}
        justifyContent="flex-end"
        sx={{ pt: 1, borderTop: `1px solid ${colors.divider}` }}
      >
        <Button
          onClick={onCancel}
          disabled={saving}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            color: colors.textMuted,
            "&:hover": { background: colors.hover },
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3.5,
            py: 1,
            color: "#fff",
            background: COLORS.primary,
            boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)",
            "&:hover": { boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)" },
            "&.Mui-disabled": {
              color: "rgba(255,255,255,0.6)",
              background: colors.cardBorder,
            },
          }}
        >
          {saving ? "Saving…" : isEdit ? "Update" : "Submit"}
        </Button>
      </Stack>
    </Stack>
  );
}

export default BankAccountFormBody;
